import React, { useEffect, useState } from "react";
import {
  EditOutlined,
  EyeOutlined,
  PlusOutlined,
  LockOutlined,
} from "@ant-design/icons";
import { BsThreeDotsVertical } from "react-icons/bs";
import { Dropdown, message } from "antd";
import GenericTable from "../../../Components/Tables/GenericTable";
import GenericModal from "../../../Components/Modals/GenericModal";
import { useNavigate } from "react-router-dom";
import "./style.css";
import { getDrivers } from "../../../API/FleetManager/Drivers";
import usePageAccess from "../../../Utlis/usePageAccess";
import { getInitials } from "../../../Components/InitialsAvatar";

const BACKEND_ORIGIN = process.env.REACT_APP_BACKEND_URL?.replace(
  /\/?api\/?$/,
  "",
);

const resolveAvatarUrl = (avatar) => {
  if (!avatar) return null;
  if (/^https?:\/\//i.test(avatar) || avatar.startsWith("blob:")) {
    return avatar;
  }
  return `${BACKEND_ORIGIN}${avatar.startsWith("/") ? "" : "/"}${avatar}`;
};

const capitalize = (str) =>
  str ? str.charAt(0).toUpperCase() + str.slice(1) : str;

const AVATAR_COLORS = [
  "#4F46E5",
  "#EA580C",
  "#16A34A",
  "#DC2626",
  "#2563EB",
  "#7C3AED",
];
const getAvatarColor = (name) =>
  AVATAR_COLORS[(name || "A").charCodeAt(0) % AVATAR_COLORS.length];

const getErrorMessage = (data, fallback) => {
  if (!data) return fallback;
  if (data.message) return data.message;
  if (data.detail) return data.detail;

  const fieldErrors = Object.values(data)
    .flat()
    .filter((msg) => typeof msg === "string");

  return fieldErrors.length ? fieldErrors.join(" ") : fallback;
};

const mapDriver = (item, index) => ({
  key: String(item.id ?? index),
  driverName: item.name || "-",
  driverImage: resolveAvatarUrl(item.avatar),
  id: item.driver_id ? ` ${item.driver_id}` : "-",
  assignedVehicle: item.assigned_vehicle?.name || "-",
  licenseNo: item.license_number || "-",
  licenseExpiry: item.license_expiry_date || "-",
  status: capitalize(item.status) || "-",
});

const Drivers = () => {
  const { canEdit } = usePageAccess("drivers");
  const [selectedRow, setSelectedRow] = useState(null);
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const [dataSource, setDataSource] = useState([]);

  const navigate = useNavigate();

  useEffect(() => {
    const fetchDrivers = async () => {
      try {
        const res = await getDrivers();
        const list = res?.data?.data;

        if (Math.floor(res?.status / 100) === 2 && Array.isArray(list)) {
          setDataSource(list.map(mapDriver));
        } else {
          message.error(getErrorMessage(res?.data, "Failed to load drivers."));
        }
      } catch (err) {
        message.error(
          getErrorMessage(err?.response?.data, "Failed to load drivers."),
        );
      }
    };

    fetchDrivers();
  }, []);

  const statusFilters = [
    ...new Set(dataSource.map((item) => item.status).filter((v) => v !== "-")),
  ].map((status) => ({
    text: status,
    value: status,
  }));

  const assignedVehicleFilters = [
    ...new Set(
      dataSource.map((item) => item.assignedVehicle).filter((v) => v !== "-"),
    ),
  ].map((val) => ({
    text: val,
    value: val,
  }));

  const columns = [
    {
      title: "Driver Name",
      dataIndex: "driverName",
      key: "driverName",
      render: (text, record) => (
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          {record.driverImage ? (
            <img
              src={record.driverImage}
              alt={text}
              style={{
                width: 32,
                height: 32,
                borderRadius: "50%",
                objectFit: "cover",
              }}
            />
          ) : (
            <span
              style={{
                width: 32,
                height: 32,
                borderRadius: "50%",
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: 12,
                fontWeight: 700,
                color: "#fff",
                background: getAvatarColor(text),
                flexShrink: 0,
              }}
            >
              {getInitials(text)}
            </span>
          )}
          <span>{text}</span>
        </div>
      ),
      enableSearch: true,
    },

    {
      title: "ID",
      dataIndex: "id",
      key: "id",
      render: (text) => <div style={{ color: "#71717A" }}>{text}</div>,
      enableSearch: true,
    },
    {
      title: "Assigned Vehicle",
      dataIndex: "assignedVehicle",
      key: "assignedVehicle",
      filters: assignedVehicleFilters,
      onFilter: (value, record) => record.assignedVehicle === value,
      render: (text) => (
        <span className={`badge-assigned ${text === "-" ? "" : "active"}`}>
          {text}
        </span>
      ),
    },
    {
      title: "License No",
      dataIndex: "licenseNo",
      key: "licenseNo",
      enableSearch: true,
    },
    {
      title: "License Expiry",
      dataIndex: "licenseExpiry",
      key: "licenseExpiry",
      sorter: (a, b) =>
        (new Date(a.licenseExpiry).getTime() || 0) -
        (new Date(b.licenseExpiry).getTime() || 0),
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      filters: statusFilters,
      onFilter: (value, record) => record.status === value,
      render: (text) => (
        <span
          className={`status-badge ${
            text === "Active" ? "active" : "inactive"
          }`}
        >
          {text}
        </span>
      ),
    },
    {
      title: "Actions",
      key: "actions",
      render: (_, record) => (
        <EyeOutlined
          style={{
            cursor: "pointer",
            color: "#4F46E5",
            fontSize: 18,
          }}
          onClick={() =>
            navigate("/driver-detail", { state: { id: record.key } })
          }
        />
      ),
    },
  ];

  const handleConfirmStatus = () => {
    if (selectedRow) {
      setDataSource((prevData) =>
        prevData.map((item) =>
          item.key === selectedRow.key ? { ...item, status: "Inactive" } : item,
        ),
      );

      setIsConfirmOpen(false);
      setSelectedRow(null);
      message.success(`${selectedRow.driverName} is now Inactive`);
    }
  };

  const handleAdd = () => {
    navigate("/add-driver");
  };

  return (
    <div className="driver-table">
      <GenericTable
        title="Drivers"
        dataSource={dataSource}
        columns={columns}
        enableRowSelection={false}
        headerButtonText={canEdit ? "Add Driver" : undefined}
        headerButtonIcon={<PlusOutlined />}
        onHeaderButtonClick={handleAdd}
      />
      <GenericModal
        open={isConfirmOpen}
        title="Inactive Status"
        subtitle={`Are you sure you want to inactive status of ${selectedRow?.driverName}? `}
        highlightText={selectedRow?.driverName}
        textareaLabel="Reason"
        textareaPlaceholder="Enter reason..."
        confirmText="Confirm Inactive"
        cancelText="Cancel"
        iconColor="#ef4444"
        iconBgColor="#ef44441a"
        confirmBtnColor="#EA580C"
        onCancel={() => setIsConfirmOpen(false)}
        onConfirm={handleConfirmStatus}
      />
    </div>
  );
};

export default Drivers;
